import { useId } from 'react'

import type { CompetitionStatusCount } from '@/api/competitions'

import { Chip } from '../list/Chip'
import { statusLabel } from './columns'
import { ACTIVE } from './params'

interface Chip {
  /** Unique among the chips: `all`, `active`, or `status:<word>`. */
  key: string
  /** What choosing it asks for; undefined for "All". */
  value: string | undefined
  label: string
  count: number
  /** Why it cannot be chosen, when it cannot. */
  unavailable?: string
}

/**
 * Why a status word cannot be asked for on its own, or none: a blank status (the API
 * takes no empty value), or a site word that is literally "active", which the link
 * vocabulary reserves for every on-sale status (`live`, `open`). It is not one of them,
 * so its competitions are reached through All.
 */
function chipUnavailable(status: string): string | undefined {
  if (!status.trim()) return 'Competitions with no status cannot be filtered on their own.'
  if (status === ACTIVE) return 'This status shares its name with the Active filter, so it cannot be filtered on its own.'
  return undefined
}

/**
 * One chip per status the API counted, with "All" and "Active" (every on-sale status)
 * first. The counts ignore the status filter, so every chip keeps its count while
 * another is chosen. A status asked for by a link but absent from the counts still
 * shows, at 0, so it can be cleared.
 */
export function StatusChips({
  byStatus,
  selected,
  onSelect,
}: {
  byStatus: readonly CompetitionStatusCount[]
  selected: string | undefined
  onSelect: (status: string | undefined) => void
}) {
  const current = selected?.toLowerCase()
  const total = byStatus.reduce((sum, entry) => sum + entry.count, 0)
  const active = byStatus.filter((entry) => entry.active).reduce((sum, entry) => sum + entry.count, 0)
  const chips: Chip[] = [
    { key: 'all', value: undefined, label: 'All', count: total },
    ...(active > 0 || current === ACTIVE ? [{ key: 'active', value: ACTIVE, label: 'Active', count: active }] : []),
    ...byStatus.map((entry) => ({
      key: `status:${entry.status}`,
      value: entry.status,
      label: statusLabel(entry.status),
      count: entry.count,
      unavailable: chipUnavailable(entry.status),
    })),
  ]
  if (current !== undefined && current !== ACTIVE && !chips.some((chip) => chip.value === current)) {
    chips.push({ key: `status:${current}`, value: current, label: statusLabel(current), count: 0 })
  }
  return (
    <div className="list-chips" role="group" aria-label="Status">
      {chips.map((chip) => (
        <Chip
          key={chip.key}
          pressed={!chip.unavailable && chip.value === current}
          disabled={chip.unavailable !== undefined}
          title={chip.unavailable}
          count={chip.count}
          onClick={() => onSelect(chip.value)}
        >
          {chip.label}
        </Chip>
      ))}
    </div>
  )
}

/**
 * The category filter. `partial` when the scope's categories could not all be read (the
 * read failed, or the list changed during it), so some may be missing, and it says so.
 */
export function CategoryFilter({
  options,
  selected,
  partial,
  onSelect,
}: {
  options: readonly string[]
  selected: string | undefined
  partial: boolean
  onSelect: (category: string | undefined) => void
}) {
  const hint = useId()
  if (options.length === 0) return null
  return (
    <span className="list-filter">
      <label className="ci-field ci-field--inline">
        <span>Category</span>
        <select
          value={selected ?? ''}
          aria-describedby={partial ? hint : undefined}
          onChange={(event) => onSelect(event.target.value || undefined)}
        >
          <option value="">All categories</option>
          {options.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </label>
      {partial ? (
        <span id={hint} className="list-muted">
          Some categories may be missing
        </span>
      ) : null}
    </span>
  )
}
